import { FC, useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { arc, DefaultArcObject, interval } from 'd3';
import { twJoin } from 'tailwind-merge';
import { CombinedError } from 'urql';

import { Icon, RyeconErrorOutline, Tooltip } from '@dbt-labs/sourdough';

import { easeOutCubic } from '@/util/tween';
import { INTERPOLATION_COLOR_MAP } from '../Recommendations/MetricSection';

const GAUGE_SCALER = 104; // base width of the gauge's outer radius (in px)

export interface ProgressGaugeProps {
  /**
   * A float between 0.0 and 1.0
   */
  value: number | undefined;
  error: CombinedError | undefined;
  isLoading: boolean;
  animationDurationMs?: number;
  testId?: string;
}

const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180;

const LOADING_ARC_RADIANS = degreesToRadians(90);
const LOADING_MILLIS_PER_ROTATION = 220;

export const ProgressGauge: FC<ProgressGaugeProps> = ({
  value,
  error,
  isLoading,
  animationDurationMs = 600,
  testId = 'progress-gauge',
}) => {
  const { width = 0, ref } = useResizeDetector();
  const radius = width / 2;
  const scale = width / GAUGE_SCALER;
  const arcWidth = 12 * scale;

  const arcOuterRadius = radius;
  const arcInnerRadius = radius - arcWidth;
  const [startAngle, setStartAngle] = useState(0);
  // percent to angle is percent * 360. Then to convert to radians you have to multiply by PI / 180
  const end = value ? degreesToRadians(value * 360) : 0;
  const fullCircle = 2 * Math.PI;

  const [currentEndAngle, setCurrentEndAngle] = useState(startAngle);
  const [displayedValue, setDisplayedValue] = useState<number | undefined>(0);

  const [color, setColor] = useState('#EF4444');

  const arcGenerator = arc<Partial<DefaultArcObject>>()
    .innerRadius(arcInnerRadius + 1)
    .outerRadius(arcOuterRadius - 1)
    .startAngle(startAngle)
    .cornerRadius(arcWidth / 2);

  const shadowArcGenerator = arc<Partial<DefaultArcObject>>()
    .innerRadius(arcInnerRadius)
    .outerRadius(arcOuterRadius)
    .startAngle(startAngle)
    .cornerRadius(arcWidth / 2);

  useEffect(() => {
    if (!isLoading) return;
    const timer = interval((elapsed) => {
      const t = elapsed / LOADING_MILLIS_PER_ROTATION;
      const targetAngle = t * 4;
      setCurrentEndAngle(targetAngle + LOADING_ARC_RADIANS);
      setStartAngle(targetAngle);
      setColor(INTERPOLATION_COLOR_MAP.redToGreen(1));
      setDisplayedValue(undefined);
    }, 1);
    return () => {
      timer.stop();
      setCurrentEndAngle(0);
      setStartAngle(0);
    };
  }, [end, value, animationDurationMs, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    if (error) {
      setCurrentEndAngle(2 * Math.PI);
      setColor('#EF4444');
      return;
    }
    const timer = interval((elapsed) => {
      const targetValue = value || 0;
      setStartAngle(0);
      const t = Math.min(1, elapsed / (animationDurationMs || 1));
      const progress = easeOutCubic(t);
      setCurrentEndAngle(startAngle + progress * (end - startAngle));
      setColor(INTERPOLATION_COLOR_MAP.redToGreen(progress * targetValue));
      setDisplayedValue(progress * targetValue);
      if (progress === 1) {
        timer.stop();
      }
    }, 1);
    return () => timer.stop();
  }, [end, value, animationDurationMs, startAngle, isLoading, error]);

  let statusTestId = `${testId}-status`;
  statusTestId += isLoading ? '-loading' : !error ? '-success' : '';
  const valueText = ((displayedValue ?? 0) * 100).toFixed();
  return (
    <div ref={ref} className="relative w-full" data-testid={testId}>
      <svg width={width} height={width}>
        <g
          className="background-bar-group"
          transform={`translate(${radius}, ${radius})`}
        >
          <path
            d={arcGenerator({ startAngle: 0, endAngle: fullCircle })!}
            opacity="0.2"
          />
        </g>
        <g transform={`translate(${radius}, ${radius})`}>
          <path
            d={shadowArcGenerator({ startAngle, endAngle: currentEndAngle })!}
            fill="transparent"
          />
          <path
            data-testid={statusTestId}
            d={arcGenerator({ startAngle, endAngle: currentEndAngle })!}
            fill={color}
          />
          {!error && (
            <g
              className={twJoin(
                'fill-fgMain font-medium transition-opacity duration-500 motion-reduce:duration-0',
                isLoading || error ? 'opacity-0' : 'opacity-100',
              )}
              data-testid={`${testId}-value`}
            >
              <text
                y={10}
                x={-6}
                transform={`scale(${scale})`}
                textAnchor="middle"
                className="text-3xl"
              >
                {valueText}
              </text>
              <text
                y={10}
                x={valueText.length === 1 ? 9 : valueText.length === 2 ? 17 : 25} // Note: move the percent sign over based on the number of digits
                transform={`scale(${scale})`}
                textAnchor="middle"
                className="text-base"
              >
                %
              </text>
            </g>
          )}
        </g>
      </svg>
      {error && (
        <Tooltip content={error?.message}>
          <Icon
            testId={`${statusTestId}-error`}
            className="absolute inset-0 mx-auto h-full text-fgDanger"
            ryecon={RyeconErrorOutline}
            size="lg"
          />
        </Tooltip>
      )}
    </div>
  );
};
